import { jsx } from '@emotion/react/jsx-runtime';
import { useTheme } from '@emotion/react';
import { createPolymorphicComponent, useSlot, usePreMeasure } from '@nettbureau/core';
import * as R from 'ramda';
import { useState, useCallback } from 'react';
import { BaseTransition } from './base-transition.js';

/**
 * Minimum and maximum scale transition values.
 */ const SCALE_LIMITS = [
    0.96,
    0.99
];
/**
 * Width thresholds that define the transition range for scale transition values.
 */ const SCALE_TRESHOLDS = [
    200,
    800
];
/**
 * Component that transitions its content in and out by collapsing and expanding its height.
 */ const CollapseInOut = createPolymorphicComponent((props, ref)=>{
    const [mergedProps, mergedRef] = useSlot('CollapseInOut', props, ref);
    const { children, dimension = 'height', disableFade = false, disableScale = false, duration = 'medium', in: visible, initial, ...elementProps } = mergedProps;
    const theme = useTheme();
    const [scale, setScale] = useState(1);
    const handleMeasure = ({ width })=>setScale(computeScale(width, SCALE_LIMITS, SCALE_TRESHOLDS));
    const [{ measuring, setMeasuring }, measureElement] = usePreMeasure(children, handleMeasure, [
        visible
    ]);
    const handleExited = useCallback(()=>setMeasuring(true), []);
    const transition = {
        duration: typeof duration === 'number' ? duration : theme.motionDurations[duration],
        ease: theme.motionTimings.standard
    };
    const enter = {
        [dimension]: 'auto',
        scale: 1,
        opacity: 1,
        transitionEnd: {
            overflow: 'visible'
        },
        transition
    };
    const exit = {
        overflow: 'hidden',
        [dimension]: 0,
        scale: disableScale ? 1 : scale,
        opacity: disableFade ? 1 : 0.5,
        transition
    };
    return /*#__PURE__*/ jsx(BaseTransition, {
        ...elementProps,
        ref: mergedRef,
        enter: enter,
        exit: exit,
        initial: initial,
        onExitComplete: handleExited,
        measuring: measuring,
        mounted: visible,
        children: measuring ? measureElement : children
    });
});
/**
 * Computes the scale value based on the element's width for balanced percentage-based transitions.
 *
 * @param width - The width of the element.
 * @param minScale - The minimum scale value.
 * @param maxScale - The maximum scale value.
 */ function computeScale(width, [minScale, maxScale], [minWidth, maxWidth]) {
    const clampedWidth = R.clamp(minWidth, maxWidth)(width);
    const scaleFactor = (clampedWidth - minWidth) / (maxWidth - minWidth);
    return minScale + scaleFactor * (maxScale - minScale);
}

export { CollapseInOut };
//# sourceMappingURL=collapse-in-out.js.map
